import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Box, Button, Chip, Fade, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable.jsx';
import APIService from '../../../api/apiService';
import { metrcTagsClasses, roomDetailClasses, tableClasses } from '../../utility/constants.jsx';
import { FeedbackContext } from '../../../App';
import SearchIcon from '@mui/icons-material/Search';
import { MetrcLogo } from '../../utility';
import MetrcTagImportBtn from '../utils/MetrcTagImportBtn.jsx';
import MetrcTagExportPopup from '../../logistics/Popup/MetrcTagExportBtn.jsx';
import MetrcTagsEditPopup from '../../logistics/Popup/MetrcTagsEditPopup.jsx';
const typeMap = {
  medical_package: 'Medical Package',
  recreational_package: 'Recreational Package',
};

const statusMap = {
  received: 'Received',
  used: 'Used',
};

function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export default function _PackageTagsTab(props) {
  const { currDarkThemeClass = '', ...otherProps } = props;

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
  const [farms, setFarms] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false); // <-- Add sync loading state

  const searchRef = useRef(null);
  const feedbackCX = useContext(FeedbackContext);

  const debouncedSearch = useCallback(
    debounce(term => {
      fetchData(0, rowsPerPage, term, selectedFilter); // Always use selectedFilter
    }, 1500),
    [rowsPerPage, selectedFilter]
  );

  useEffect(() => {
    return () => {
      if (searchRef.current) {
        clearTimeout(searchRef.current);
      }
    };
  }, []);

  const handleEditClick = tag => {
    setSelectedTag(tag);
    setIsEditPopupOpen(true);
  };

  const formatData = data => {
    return data.map((item, idx) => [
      idx + 1,
      item.tag || '',
      typeMap[item.type] || item.type,
      statusMap[item.status] || item.status,
      // item.commissioned,
      // item.used_on,
      // <DetailAndDelBtn ... />
    ]);
  };

  // Update endpoint to package-tag
  const fetchData = async (page, limit, searchTerm = '', farmId = selectedFilter) => {
    setLoading(true);
    try {
      let url = `metrc-tag/?limit=${limit}&page=${page + 1}&type=medical_package`;
      if (farmId) {
        url += `&farm_id=${farmId}`;
      }
      if (searchTerm) {
        url += `&tag__icontains=${encodeURIComponent(searchTerm)}`;
      }
      const response = await APIService.fetchInstance(url);
      const formattedData = formatData(response?.data?.tags ?? []);
      setData(formattedData);
      setTotalCount(response?.data?.total_tags ?? 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, selectedFilter]);

  useEffect(() => {
    if (farms.length === 0) {
      (async () => {
        const farmsResponse = await APIService.fetchInstance('collection/farm/');
        const results = farmsResponse?.data?.farms?.results || [];
        const farmOptions = results.map(farm => ({
          id: farm?.id,
          name: farm?.farm_name,
        }));
        setFarms(farmOptions);
        if (farmOptions.length > 0 && !selectedFilter) {
          setSelectedFilter(farmOptions[0].id);
        }
      })();
    }
    // eslint-disable-next-line
  }, []);

  // --- Sync from Metrc logic ---
  const handleSyncFromMetrc = async () => {
    setSyncLoading(true);
    try {
      await APIService.fetchInstance(`metrc-tag/?sync_to_metrc=true&farm_id=${selectedFilter}&type=medical_package`);
      await fetchData(page, rowsPerPage, searchTerm);
      feedbackCX?.setContextValue(true, {
        type: 'success',
        message: 'Package Tags Synced from Metrc successfully!',
      });
    } catch (err) {
      feedbackCX?.setContextValue(true, {
        type: 'error',
        message: 'Sync from Metrc failed.',
      });
    }
    setSyncLoading(false);
  };
  // --- End Sync logic ---

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const tagKeyTitleMap = [
    { key: 'id', title: 'Sl No' },
    { key: 'tag', title: 'Tag' },
    { key: 'type', title: 'Type' },
    { key: 'status', title: 'Status' },
    // { key: 'commissioned', title: 'Commissioned' },
    // { key: 'used_on', title: 'Used' },
    // { key: 'action', title: ' ' },
  ];

  const tableTitles = tagKeyTitleMap.map(e => ({ title: e.title }));

  const displayProps = {
    currDarkThemeClass,
    tableEntries: data,
    tableTitles,
    totalCount,
    onPageChange: handlePageChange,
    loading,
    rowsPerPage,
    page,
    entity: 'Package Tags',
    ...otherProps,
  };

  const handleTagSave = async updatedTag => {
    try {
      const response = await APIService.modifyInstance(`package-tag/${updatedTag.id}`, updatedTag);
      setIsEditPopupOpen(false);
      if (response.status === 200) {
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Tag updated successfully.',
        });
        fetchData(page, rowsPerPage);
      }
    } catch (error) {
      setIsEditPopupOpen(false);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error updating tag',
      });
      return { success: false };
    }
  };

  const handlePopupClose = () => {
    setIsEditPopupOpen(false);
    setSelectedTag(null);
  };

  const handleSetSelTag = (prop, value) => {
    setSelectedTag(prevTag => ({
      ...prevTag,
      [prop]: value,
    }));
  };

  const handleFilterChange = farmId => {
    if (farmId !== selectedFilter) {
      setPage(0);
      setSelectedFilter(farmId);
    }
  };

  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };

  const sChipsProps = farm => ({
    key: farm.id,
    label: farm.name,
    variant: selectedFilter === farm.id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const metrcTagFarmChipsList = (
    <Stack direction="row" spacing={1}>
      {farms.map(farm => (
        <Chip key={farm.id} {...sChipsProps(farm)} />
      ))}
    </Stack>
  );

  const handleImportComplete = () => {
    fetchData(page, rowsPerPage);
  };

  const metrcTagsEditPopupProps = {
    currDarkThemeClass,
    open: isEditPopupOpen,
    onClose: handlePopupClose,
    selTag: selectedTag,
    handleSetSelTag: handleSetSelTag,
    handleTagSave: handleTagSave,
    toShowSave: true,
    title: 'Edit Tag',
    farms,
    ...otherProps,
  };

  const metrcTagImportBtnProps = {
    currDarkThemeClass,
    onImportComplete: handleImportComplete,
    farms: farms,
  };

  const handleSensorSearch = ({ target: { value } }) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Package Tag',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSensorSearch,
    value: searchTerm,
  };

  const handleExportPopupOpen = () => {
    setIsExportPopupOpen(true);
  };

  const handleExportPopupClose = () => {
    setIsExportPopupOpen(false);
  };

  const metrcTagExportBtnProps = {
    currDarkThemeClass,
    open: isExportPopupOpen,
    onClose: handleExportPopupClose,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>
              Show by Farm:
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>
          <Stack direction="row" gap={2} sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncFromMetrc}
              disabled={syncLoading}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>
            <MetrcTagImportBtn {...metrcTagImportBtnProps} />
            <Button onClick={handleExportPopupOpen}>Export</Button>
            <MetrcTagExportPopup {...metrcTagExportBtnProps} />
            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...displayProps} />
        </Box>
        {isEditPopupOpen && <MetrcTagsEditPopup {...metrcTagsEditPopupProps} />}
      </Box>
    </Fade>
  );
}
